/**
 * The AI Team page's views of its findings (ADR 0259, issue #682): each
 * employee's counts, the jobs with something open, and the jobs whose
 * findings were all dismissed. `aiTeam.ts` makes the findings.
 */

import {
  AI_EMPLOYEE_ORDER,
  AI_MONTH_DAYS,
  aiWithinDays,
  type AiEmployee,
  type AiFinding,
} from "./aiTeam.js";

const DAY = 86_400_000;

export interface AiEmployeeCount {
  open: number;
  /** Raised in the last 30 days, open or not. */
  flagged: number;
  /** Of those dismissed in the last 30 days, marked wrong and right but fine. */
  wrong: number;
  fine: number;
}

/** Each employee's open count and this month's record. */
export function aiTeamCounts(
  findings: readonly AiFinding[],
  now: Date,
): Record<AiEmployee, AiEmployeeCount> {
  const counts = Object.fromEntries(
    AI_EMPLOYEE_ORDER.map((e) => [
      e,
      { open: 0, flagged: 0, wrong: 0, fine: 0 },
    ]),
  ) as Record<AiEmployee, AiEmployeeCount>;
  for (const finding of findings) {
    const count = counts[finding.employee];
    if (finding.status === "open") count.open += 1;
    if (aiWithinDays(finding.raisedAt, now, AI_MONTH_DAYS)) count.flagged += 1;
    if (
      finding.status === "dismissed" &&
      aiWithinDays(finding.dismissedAt, now, AI_MONTH_DAYS)
    ) {
      if (finding.reason === "wrong") count.wrong += 1;
      if (finding.reason === "fine") count.fine += 1;
    }
  }
  return counts;
}

export interface AiJobGroup {
  jobId: string;
  open: AiFinding[];
  dismissed: AiFinding[];
  /** When its oldest open finding was raised. */
  oldest: string;
}

/**
 * The jobs with an open finding, the one waiting longest first, each with its
 * dismissed history. A finding with no job is left to the feed.
 */
export function aiFindingsByJob(findings: readonly AiFinding[]): AiJobGroup[] {
  return groupByJob(findings)
    .filter((group) => group.open.length > 0)
    .sort((a, b) => a.oldest.localeCompare(b.oldest));
}

/**
 * The jobs whose findings were all dismissed, the latest dismissal first: the
 * "Dismissed in the last 90 days" list beside the open ones.
 */
export function aiDismissedJobs(findings: readonly AiFinding[]): AiJobGroup[] {
  const latest = (group: AiJobGroup) =>
    group.dismissed.reduce(
      (max, f) => ((f.dismissedAt ?? "") > max ? (f.dismissedAt ?? "") : max),
      "",
    );
  return groupByJob(findings)
    .filter((group) => group.open.length === 0)
    .sort((a, b) => latest(b).localeCompare(latest(a)));
}

function groupByJob(findings: readonly AiFinding[]): AiJobGroup[] {
  const groups = new Map<string, AiJobGroup>();
  for (const finding of findings) {
    if (!finding.jobId) continue;
    const group = groups.get(finding.jobId) ?? {
      jobId: finding.jobId,
      open: [],
      dismissed: [],
      oldest: "",
    };
    if (finding.status === "open") {
      group.open.push(finding);
      if (!group.oldest || finding.raisedAt < group.oldest) {
        group.oldest = finding.raisedAt;
      }
    } else {
      group.dismissed.push(finding);
    }
    groups.set(finding.jobId, group);
  }
  return [...groups.values()];
}

/** Whole days since a finding was raised, for "waiting 9 days". */
export function aiWaitingDays(finding: AiFinding, now: Date): number {
  return Math.max(
    0,
    Math.floor((now.getTime() - new Date(finding.raisedAt).getTime()) / DAY),
  );
}
